import React from 'react';
import * as RadixSelect from '@radix-ui/react-select';
import { ChevronDown, Check } from 'lucide-react';
import { useRadixSelectAnimation } from '../../hooks/useRadixSelectAnimation';
import {
  FORM_BORDER,
  FORM_BORDER_HOVER,
  FORM_BORDER_ERROR,
  FOCUS_RING,
  FORM_RADIUS,
  FORM_SHADOW,
  DROPDOWN_ANIMATION,
  DROPDOWN_BG,
  ITEM_HOVER,
  LABEL_TEXT,
  ERROR_TEXT,
} from './tokens';

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

interface SelectProps {
  readonly options: readonly SelectOption[];
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly error?: string;
  readonly label?: string;
  readonly required?: boolean;
  readonly className?: string;
  /** Custom renderer for each option row in the dropdown. `label` is always in `ItemText` (sr-only) for accessibility. */
  readonly renderItem?: (option: SelectOption) => React.ReactNode;
  /** Custom renderer for the selected value shown in the trigger button. */
  readonly renderValue?: (option: SelectOption | undefined) => React.ReactNode;
}

export const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Seleccionar...',
  disabled = false,
  error,
  label,
  required = false,
  className = '',
  renderItem,
  renderValue,
}) => {
  const { status, handleOpenChange, handleAnimationEnd } = useRadixSelectAnimation();

  const triggerClasses = [
    'w-full px-4 py-3',
    'flex items-center justify-between',
    'bg-white',
    FORM_RADIUS,
    error ? FORM_BORDER_ERROR : FORM_BORDER,
    !disabled && FORM_BORDER_HOVER,
    FOCUS_RING,
    'data-[state=open]:ring-2 data-[state=open]:ring-blue-500 data-[state=open]:ring-offset-1 data-[state=open]:outline-none',
    disabled ? 'bg-slate-50 cursor-not-allowed' : 'cursor-pointer active:scale-[0.98]',
    'text-sm text-left transition',
    'outline-none',
  ]
    .filter(Boolean)
    .join(' ');

  const contentClasses = [
    'z-[200] min-w-[var(--radix-select-trigger-width)] overflow-hidden',
    FORM_RADIUS,
    FORM_SHADOW,
    DROPDOWN_ANIMATION,
    DROPDOWN_BG,
  ].join(' ');

  const itemClasses = [
    'relative flex items-center justify-between gap-2',
    'px-3 py-2 text-sm text-slate-700 rounded-lg cursor-pointer select-none outline-none',
    ITEM_HOVER,
    'data-[highlighted]:bg-slate-50',
    'data-[state=checked]:font-semibold data-[state=checked]:text-slate-900',
    'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
  ].join(' ');

  const selectedLabel = options.find((o) => o.value === value)?.label;

  return (
    <div className={className}>
      {label && (
        <label className={`block ${LABEL_TEXT} mb-1.5`}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <RadixSelect.Root
        open={status !== 'closed'}
        onOpenChange={handleOpenChange}
        value={value}
        onValueChange={(v) => { if (v) onChange(v); }}
        disabled={disabled}
      >
        <RadixSelect.Trigger className={triggerClasses}>
          <RadixSelect.Value placeholder={placeholder}>
            {renderValue
              ? (renderValue(options.find(o => o.value === value)) ?? placeholder)
              : (selectedLabel ?? placeholder)}
          </RadixSelect.Value>
          <RadixSelect.Icon asChild>
            <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-150 ${status === 'closed' ? '' : 'rotate-180'}`} />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>

        {status !== 'closed' && (
          <RadixSelect.Portal>
            <RadixSelect.Content
              position="popper"
              sideOffset={6}
              data-state={status === 'closing' ? 'closed' : 'open'}
              data-dropdown-portal="true"
              onAnimationEnd={handleAnimationEnd}
              className={`${contentClasses} dropdown-portal-protected`}
            >
              <RadixSelect.Viewport className="p-1">
                {options.map((option) => (
                  <RadixSelect.Item
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className={itemClasses}
                  >
                    {renderItem ? (
                      <>
                        <RadixSelect.ItemText className="sr-only">{option.label}</RadixSelect.ItemText>
                        {renderItem(option)}
                      </>
                    ) : (
                      <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                    )}
                    <RadixSelect.ItemIndicator>
                      <Check className="w-3.5 h-3.5 text-blue-600" />
                    </RadixSelect.ItemIndicator>
                  </RadixSelect.Item>
                ))}
              </RadixSelect.Viewport>
            </RadixSelect.Content>
          </RadixSelect.Portal>
        )}
      </RadixSelect.Root>

      {error && <p className={ERROR_TEXT}>{error}</p>}
    </div>
  );
};
